import { supabase } from '../supabase';

// Sends the website forms to the "forms" server function (see supabase/functions/forms).
// Every function here returns when the form was saved, or throws an Error with a message that is safe to show visitors.

const FALLBACK = 'We could not send this right now. Please try again in a little while.';

const readError = async (error) => {
  try {
    const body = await error.context.json();
    return body && body.error ? body.error : null;
  } catch (e) {
    return null;
  }
};

const send = async (body) => {
  const { data, error } = await supabase.functions.invoke('forms', { body });
  if (error) throw new Error((await readError(error)) || FALLBACK);
  if (!data || data.ok !== true) throw new Error((data && data.error) || FALLBACK);
  return data;
};

// "website" is a hidden trap field: people never see it, bots fill it in, and the server then saves nothing.
export const submitInquiry = (fields) => send({ type: 'inquiry', ...fields });

export const subscribeNewsletter = (email, website = '') => send({ type: 'newsletter', email, website });

export const registerForEvent = (fields) => send({ type: 'registration', ...fields });

// Saves the application, then sends each document straight to private storage with the one-time links the server gives back.
// Resolves with { reference, failedFiles }. Documents that did not upload are listed so the visitor can send them by email.
export const submitApplication = async (fields, files = []) => {
  const data = await send({
    type: 'application',
    ...fields,
    documents: files.map((f) => ({ name: f.name, type: f.type, size: f.size })),
  });
  const failedFiles = [];
  for (let i = 0; i < files.length; i += 1) {
    const link = data.uploads && data.uploads[i];
    if (!link) {
      failedFiles.push(files[i].name);
      continue;
    }
    const { error } = await supabase.storage
      .from('application-documents')
      .uploadToSignedUrl(link.path, link.token, files[i], { contentType: files[i].type });
    if (error) failedFiles.push(files[i].name);
  }
  return { reference: data.reference, failedFiles };
};

// Reads the values of a form (inputs with a name) as a plain object. Empty values stay empty strings.
export const formValues = (form) => Object.fromEntries(new FormData(form).entries());
